import { useState } from 'react';
import {  useNavigate } from 'react-router-dom';
import moment from 'moment';
import axiosClient from '../../../api/axiosClient';
import { useTour } from '../../../context/TourContext';

import useAuth from '../../../auth/useAuth';

import './styles.css';

import Alert from '../alert';

import IconChecked from './images/icon-checked.svg';
import IconUnchecked from './images/icon-unchecked.svg';

import AssignmentInfo from './info';
import PerformanceRankingInfo from './../performanceRankingInfo';

import { useIsInstructorContainingPage } from '../../../utils';

import BadgeMaster from './images/badge-master.png';
import BadgeWarrior from './images/badge-warrior.png';
import BadgeNewbie from './images/badge-newbie.png';
import BadgeNoob from './images/badge-noob.png';

export default function AssignmentItem({
  assignment,
  assignmentType,
  onAssignmentDeletionConfirm,
  currentGameSessions,
  currentGameScore,
  rank
}) {
  const navigate = useNavigate();

  const { saveCurrentGameId, isInstructor } = useAuth();
  const { isActive: isTourActive, endTour } = useTour();

  const tourGameId = sessionStorage.getItem('onboardingTourGameId');
  const isTourTarget = tourGameId && String(assignment.id) === tourGameId;

  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isNoLevelsAlertVisible, setIsNoLevelsAlertVisible] = useState(false);

  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const handleEditButtonClick = () => {
    if (isTourActive && isTourTarget) endTour();
    if(assignmentType === 'review-sessions') {
      navigate(`/instructor/settings/tournament/edit/${assignment.id}`);
    } else {
      navigate(`/instructor/settings/assignment/edit/${assignment.id}`);
    }
  }

  const handleDeleteAssignmentButtonClick = () => {
    if (isTourActive && isTourTarget) endTour();
    setIsAlertVisible(true);
  }

  const handleConfirmAlertButtonClick = () => {
    if (typeof onAssignmentDeletionConfirm !== 'function') {
      console.warn('Assignment deletion handler not wired for this page');
      handleCloseAlert();
      return;
    }
    onAssignmentDeletionConfirm(assignment.id, handleCloseAlert);
  }

  const handleCloseAlert = () => {
    setIsAlertVisible(false);
  }

  function formatDate(endTime) {
    return moment(endTime).format('MMM D [at] h:mm a');
  }

  const handlePreviewButtonClick = async () => {
    if (isTourActive && isTourTarget) endTour();
    if(assignmentType === 'review-sessions') {
      if(isInstructor()) {
        window.open(`/instructor/review-session/${assignment.id}`, '_blank');
      } else {
        window.open(`/review-session/${assignment.id}`, '_blank');
      }
    } else {
      if (assignment.scenario) {
        try {
          const scenarioLevelsUrl = isInstructor()
            ? `/api/games/instructor/scenario-levels/?scenarios=${assignment.scenario}`
            : `/api/games/scenario-levels/?scenarios=${assignment.scenario}`;
          const response = await axiosClient.get(scenarioLevelsUrl);
          const levels = response.data?.results || response.data || [];
          if (!levels.length) {
            setIsNoLevelsAlertVisible(true);
            return;
          }
        } catch (error) {
          console.error('Error checking scenario levels:', error);
        }
      }

      const gameType = assignment.game_type;
      saveCurrentGameId(assignment.id, gameType);

      window.open('/acc/transition/', '_blank');
    }
  }

  const handleInfoButtonMouseEnter = () => {
    setIsInfoVisible(true);
  }

  const handleInfoButtonMouseLeave = () => {
    setIsInfoVisible(false);
  }

  const handlePerformanceRankingInfoButtonMouseEnter = () => {
    setIsPerformanceRankingInfoVisible(true);
  }

  const handlePerformanceRankingInfoButtonMouseLeave = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  const handleViewResultsButtonClick = () => {
    saveCurrentGameId(assignment.id, assignment.game_type);
    window.open('/classification/feedback', '_blank');
  }

  const isInstructorPage = useIsInstructorContainingPage();

  const isSessionCompleted = currentGameSessions.some(session => session.completed);
  const isSessionIncompleted = currentGameSessions.some(session => !session.completed);
  let assignmentButtonText = 'Start';
  let isAssignmentButtonDisabled = false;
  let assignmentButtonTitle = '';

  if(assignmentType === 'review-sessions' && isInstructor()) {
    assignmentButtonText = 'Access';
  } else if (currentGameSessions.length) {
    if(currentGameSessions.length >= 3 && !isSessionIncompleted && !isInstructor()) {
      assignmentButtonText = 'Unavailable';
      isAssignmentButtonDisabled = true;
      assignmentButtonTitle = "You've completed all 3 attempts.";
    } else {
      if(isSessionIncompleted) {
        assignmentButtonText = 'Continue';
      } else {
        assignmentButtonText = 'Try Again?';
      }
    }
  }

  const currentTime = moment();
  const startTime = moment(assignment.start_time);
  const endTime = moment(assignment.end_time);
  if(assignmentType !== 'review-sessions') {
    if((currentTime.startOf('minute').isBefore(startTime.startOf('minute')) || currentTime.startOf('minute').isAfter(endTime.startOf('minute')) || (currentGameSessions.length >= 3 && !isSessionIncompleted)) && !isInstructor()) {
      assignmentButtonText = 'Unavailable';
      isAssignmentButtonDisabled = true;
      assignmentButtonTitle = currentTime.isBefore(startTime)
        ? 'Assignment is not open yet.'
        : currentTime.isAfter(endTime)
          ? 'Assignment is now closed.'
          : "You've completed all 3 attempts.";
    }

  }

  const badgeImages = {
    Master: BadgeMaster,
    Warrior: BadgeWarrior,
    Newbie: BadgeNewbie,
    Noob: BadgeNoob,
  };

  const openDateTime = assignment.start_time ? formatDate(assignment.start_time) : '---';
  const closeDateTime = assignment.end_time ? formatDate(assignment.end_time) : '---';

  return (
    <div
      className="assignment-item"
      data-assignment-id={assignment.id}
      {...(isTourTarget ? { 'data-tour-id': 'assignment-created' } : {})}
    >
      {
        (isInstructor() && isInstructorPage) &&
          <button className="edit-assignment" onClick={handleEditButtonClick}></button>
      }
      {
        !isInstructorPage &&
          <img 
            className="assignment-session-completed-status"
            src={isSessionCompleted ? IconChecked : IconUnchecked}
            alt={isSessionCompleted ? 'Completed' : 'Not Completed'}
          />
      }
      <div className="assignment-item-info">
        <div className="assignment-item-info-heading">
          <h4>{assignment.name}</h4>
          {
            isInstructor() && onAssignmentDeletionConfirm &&
              <button className="delete-assignment" onClick={handleDeleteAssignmentButtonClick}></button>
          }
          {
            !isInstructorPage &&
              <div className="assignment-info-container">
                <button 
                  className="assignment-info-icon"
                  onMouseEnter={handleInfoButtonMouseEnter}
                  onMouseLeave={handleInfoButtonMouseLeave}
                ></button>
                {
                  isInfoVisible &&
                    <AssignmentInfo title={assignment.name} description={assignment.description} />
                }
              </div>
          }
        </div>
        {
          assignmentType !== 'review-sessions' && 
            <div className="assignment-item-info-footer">
              <p>
                <span>Open </span>{openDateTime} <span>| Close</span> {closeDateTime}
              </p>
            </div>
        }
      </div>

      {
        isAlertVisible &&
          <Alert
            type="warning"
            title={`Are you sure you want to delete ${assignment.name}?`}
            confirmButtonText="Yes"
            cancelButtonText="No"
            onCancelButtonClick={handleCloseAlert}
            onConfirmButtonClick={handleConfirmAlertButtonClick}
          />
      }

      {
        isNoLevelsAlertVisible &&
          <Alert
            type="warning"
            title={isInstructor() ? 'Add a level to the Assignment' : 'Your instructor needs to add a level before you can start the assignment.'}
            confirmButtonText="OK"
            onConfirmButtonClick={() => setIsNoLevelsAlertVisible(false)}
          />
      }

      <div className="assignment-right-part">
        {
          (!isInstructorPage && currentGameScore > 0) &&
            <div className="assignment-last-results-container">
              <button 
                className="assignment-last-results"
                onMouseEnter={handlePerformanceRankingInfoButtonMouseEnter}
                onMouseLeave={handlePerformanceRankingInfoButtonMouseLeave}
              >
                <span className="assignment-points">{currentGameScore.toLocaleString('en-US')}</span>
                <span className="assignment-badge"><img className="badge-image" src={badgeImages[rank]} alt={`${rank} Badge`} /></span>
              </button>
              {
                isPerformanceRankingInfoVisible &&
                  <PerformanceRankingInfo />
              }
            </div>
        }

        <button 
          className="preview-assignment button-navi"
          onClick={handlePreviewButtonClick}
          disabled={isAssignmentButtonDisabled}
          title={assignmentButtonTitle}
        >
          {assignmentButtonText}
        </button>
      </div>
    </div>
  );
}
